import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';

import { TenantService } from '../services/tenant.service';
import { UserAdminService } from '../services/user-admin.service';
import { MemberSummary } from '../models/member.model';
import { AdminTenantDetailComponent } from '../tenant-detail/tenant-detail.component';
import { Grid } from '../../../../shared/components/grid/grid';
import { GridPage } from '../../../../shared/components/grid/grid-page';
import { GridColumn, GridRowAction, gridColumns } from '../../../../shared/components/grid/models/grid.model';
import { BreadcrumbService } from '../../../../shared/components/breadcrumbs/breadcrumb.service';
import { ModalService } from '../../../../shared/components/modal/modal.service';
import { ToastService } from '../../../../shared/services/toast-service';
import { LanguageService } from '../../../../core/i18n/language.service';

/**
 * Users of one tenant (/admin/tenants/:tenantId/users) — super-admin only.
 * Rendered as the Users tab inside AdminTenantDetailComponent's shell (which
 * owns the tenant name header + breadcrumb label); row click drills further
 * into the full-page user detail view. Rows are MemberSummary from
 * /organizations/{id}/members; the kebab exposes the per-user admin actions
 * (/users/{id}/...). Search is server-side (total best-effort while set).
 */
@Component({
  selector: 'app-admin-tenant-users',
  standalone: true,
  imports: [Grid, GridPage],
  templateUrl: './tenant-users.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTenantUsersComponent {
  private readonly tenantService = inject(TenantService);
  private readonly userAdmin = inject(UserAdminService);
  private readonly shell = inject(AdminTenantDetailComponent);
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly modal = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // NOT read from this.route: this route ('users') has a non-empty path and a
  // componentful parent (the shell), so under the router's default 'emptyOnly'
  // strategy it does NOT inherit :tenantId — the lookup here returned null and
  // produced /organizations//members. The shell sits at a path-less route where
  // inheritance does apply, so its id is authoritative.
  private readonly tenantId = this.shell.tenantId;

  readonly users = signal<MemberSummary[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly isLoading = signal(false);
  readonly searchTerm = signal('');

  /** Stable row id for tracking. */
  readonly userId = (user: MemberSummary) => user.id;
  /** Narrows the erased-generic row that grid events hand back. */
  readonly asUser = (row: unknown): MemberSummary => row as MemberSummary;

  /** Columns as a computed so labels re-translate on a language switch.
   *  Not sortable — the members endpoint has no sort support. */
  readonly columns = computed<GridColumn<MemberSummary>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`admin.tenantUsers.columns.${k}`);
    const status = (k: string) => this.transloco.translate(`admin.tenantUsers.status.${k}`);
    return gridColumns<MemberSummary>([
      // Email leads — it IS the login identifier (EmailAsUsername realm).
      { key: 'email', label: t('email'), type: 'text', textVariant: 'strong', alwaysVisible: true, width: 260 },
      { key: 'firstName', label: t('firstName'), type: 'text', hideOnCompact: true },
      { key: 'lastName', label: t('lastName'), type: 'text', hideOnCompact: true },
      {
        key: 'status',
        label: t('status'),
        type: 'status',
        width: 130,
        status: (value) => {
          switch (value) {
            case 'INVITED':
              return { label: status('invited'), color: 'orange' };
            case 'LOCKED':
              return { label: status('locked'), color: 'red' };
            case 'DISABLED':
              return { label: status('disabled'), color: 'neutral', hollow: true };
            default:
              return { label: status('active'), color: 'green' };
          }
        },
      },
      // null = no session data (Keycloak keeps active sessions only) — the
      // grid's date cell renders its "—" fallback, deliberately NOT "never".
      { key: 'lastLogin', label: t('lastLogin'), type: 'date', width: 180 },
      { key: 'createdAt', label: t('createdAt'), type: 'date', width: 180, hiddenByDefault: true },
      { key: 'mfaEnabled', label: t('mfa'), type: 'boolean', width: 90, hiddenByDefault: true },
    ]);
  });

  /** Kebab actions backed by the per-user admin endpoints. */
  readonly rowActions = computed<GridRowAction<MemberSummary>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`admin.tenantUsers.actions.${k}`);
    return [
      {
        id: 'resendInvite',
        label: t('resendInvite'),
        icon: 'icon-user-invite',
        placement: 'menu',
        disabled: (row) => row.status !== 'INVITED',
      },
      { id: 'resetPassword', label: t('resetPassword'), icon: 'icon-sso', placement: 'menu' },
      { id: 'signOut', label: t('signOut'), icon: 'icon-disable-ban', placement: 'menu' },
      {
        id: 'unlock',
        label: t('unlock'),
        icon: 'icon-checkmark',
        placement: 'menu',
        disabled: (row) => row.status !== 'LOCKED',
      },
      { id: 'remove', label: t('remove'), icon: 'icon-delete-trash', placement: 'menu', danger: true },
    ];
  });

  /** Initial fetch + refetch when the shell reports a membership change
   *  (e.g. an invite sent from the header while this tab is open). */
  private readonly membersOnChange = effect(() => {
    this.shell.memberChange();
    untracked(() => this.fetch());
  });

  onRowClick(user: MemberSummary): void {
    // Name the crumb before navigating so the trail never flashes a raw id.
    this.breadcrumbs.setLabel('userId', user.id, user.email);
    this.router.navigate([user.id], { relativeTo: this.route });
  }

  onRowAction(actionId: string, user: MemberSummary): void {
    switch (actionId) {
      case 'resendInvite':
        this.runUserAction(this.userAdmin.resendInvite(user.id), 'inviteResent', 'inviteResendFailed');
        break;
      case 'resetPassword':
        this.runUserAction(this.userAdmin.sendResetPasswordEmail(user.id), 'resetSent', 'resetFailed');
        break;
      case 'signOut':
        this.runUserAction(this.userAdmin.signOutEverywhere(user.id), 'signedOut', 'signOutFailed');
        break;
      case 'unlock':
        this.runUserAction(this.userAdmin.unlock(user.id), 'unlocked', 'unlockFailed', () => this.fetch());
        break;
      case 'remove':
        void this.confirmRemove(user);
        break;
    }
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.page.set(0);
    this.fetch();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetch();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.fetch();
  }

  private async confirmRemove(user: MemberSummary): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('admin.tenantUsers.remove.title'),
      message: this.transloco.translate('admin.tenantUsers.remove.message', { email: user.email }),
      confirmLabel: this.transloco.translate('admin.tenantUsers.remove.confirm'),
    });
    if (!confirmed) return;
    this.runUserAction(this.tenantService.removeMember(this.tenantId, user.id), 'removed', 'removeFailed', () =>
      this.shell.memberChange.update((n) => n + 1),
    );
  }

  private runUserAction(action: Observable<unknown>, successKey: string, errorKey: string, onSuccess?: () => void): void {
    action.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show(this.transloco.translate(`admin.tenantUsers.toast.${successKey}`), 'success');
        onSuccess?.();
      },
      error: () => this.toastService.show(this.transloco.translate(`admin.tenantUsers.toast.${errorKey}`), 'error'),
    });
  }

  private fetch(): void {
    // Never emit /organizations//members — an empty id would slip past the
    // backend's @NotBlank because the collapsed URL matches GET /organizations/{id}.
    if (!this.tenantId) return;
    this.isLoading.set(true);
    this.tenantService
      .getTenantUsers(this.tenantId, this.page(), this.size(), this.searchTerm().trim() || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.users.set(res.data);
          this.totalElements.set(res.totalElements);
          this.totalPages.set(res.totalPages);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('admin.tenantUsers.toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }
}
